import { formatCents } from '@auctisync/shared';
import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import { embedTitle } from '../lib/embedTitle';

type CompRow = {
  id: string;
  auction_number: number | null;
  lot_number: string | null;
  title: string | null;
  sold_at: string | null;
  final_price_cents: number | null;
  msrp_cents: number | null;
  sell_through_ratio: number | null;
  source_category_name: string | null;
  brand: string | null;
  mpn: string | null;
  sold: boolean;
  similarity_score: number | null;
  semantic_similarity: number | null;
  match_tier: number | null;
  match_tier_description: string | null;
  source_system: string | null;
};

type Props = {
  tenantId: string;
  title: string;
  mpn?: string | null | undefined;
  categoryId?: string | null | undefined;
  categoryName?: string | null | undefined;
  condition?: string | null | undefined;
  onClose: () => void;
};

type SortKey = 'similarity' | 'date' | 'price';

function shortDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  return d.toLocaleDateString('en-US', { year: '2-digit', month: 'short', day: 'numeric' });
}

function formatSimilarity(s: number | null): string {
  if (s == null) return '—';
  return `${Math.round(s * 100)}%`;
}

function formatRatio(r: number | null): string {
  if (r == null) return '—';
  return `${Math.round(r * 100)}%`;
}

function TierBadge({ tier, label }: { tier: number | null; label: string | null }) {
  if (tier == null) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
  // Soft backgrounds with dark text — readable and progressively de-emphasized
  // as match confidence drops (emerald → blue → gray → amber).
  const colors: Record<number, { bg: string; fg: string; border: string }> = {
    1: { bg: '#d1fae5', fg: '#065f46', border: '#a7f3d0' }, // exact model — emerald
    2: { bg: '#dbeafe', fg: '#1e3a8a', border: '#bfdbfe' }, // same brand — blue
    3: { bg: '#ede9fe', fg: '#5b21b6', border: '#ddd6fe' }, // semantic match — violet
    4: { bg: '#f3f4f6', fg: '#374151', border: '#e5e7eb' }, // same category — neutral
    5: { bg: '#fef3c7', fg: '#92400e', border: '#fde68a' }, // title trigram — amber
  };
  const c = colors[tier] ?? { bg: '#f3f4f6', fg: '#4b5563', border: '#e5e7eb' };
  return (
    <span
      style={{
        display: 'inline-block',
        padding: '0.15rem 0.5rem',
        borderRadius: '9999px',
        fontSize: '0.7rem',
        fontWeight: 600,
        background: c.bg,
        color: c.fg,
        border: `1px solid ${c.border}`,
        whiteSpace: 'nowrap',
      }}
    >
      {label ?? `T${tier}`}
    </span>
  );
}

/**
 * Detail modal behind the "Your comps" strip. Shows the individual historical
 * rows that fed the aggregated stats so staff can sanity-check matches.
 */
export function SalesCompsModal({
  tenantId,
  title,
  mpn,
  categoryId,
  categoryName,
  condition,
  onClose,
}: Props) {
  const [rows, setRows] = useState<CompRow[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sortKey, setSortKey] = useState<SortKey>('similarity');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        // Compute semantic embedding first (cached by embedTitle lib).
        const embedding = await embedTitle(title);
        if (cancelled) return;
        // Cast via unknown — list_sales_comps is not yet in generated DB types.
        // TODO: regenerate types (pnpm db:types) next time the Supabase CLI login works.
        const { data, error: e } = await (
          supabase.rpc as unknown as (
            fn: string,
            args: Record<string, unknown>,
          ) => Promise<{ data: CompRow[] | null; error: Error | null }>
        )('list_sales_comps', {
          p_tenant_id: tenantId,
          p_title: title,
          ...(mpn ? { p_mpn: mpn } : {}),
          ...(categoryId ? { p_category_id: categoryId } : {}),
          ...(categoryName ? { p_category_name: categoryName } : {}),
          ...(condition ? { p_condition: condition } : {}),
          ...(embedding ? { p_query_embedding: embedding } : {}),
          p_limit: 100,
        });
        if (cancelled) return;
        if (e) throw e;
        setRows(data ?? []);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Could not load comps');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [tenantId, title, mpn, categoryId, categoryName, condition]);

  // Dismiss on Escape
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  const sortedRows = useMemo(() => {
    if (!rows) return [];
    const copy = [...rows];
    if (sortKey === 'similarity') {
      copy.sort((a, b) => (b.similarity_score ?? 0) - (a.similarity_score ?? 0));
    } else if (sortKey === 'date') {
      copy.sort((a, b) => {
        const ta = a.sold_at ? new Date(a.sold_at).getTime() : 0;
        const tb = b.sold_at ? new Date(b.sold_at).getTime() : 0;
        return tb - ta;
      });
    } else if (sortKey === 'price') {
      copy.sort((a, b) => (b.final_price_cents ?? 0) - (a.final_price_cents ?? 0));
    }
    return copy;
  }, [rows, sortKey]);

  return (
    <div
      style={backdropStyle}
      role="dialog"
      aria-modal="true"
      aria-label="Sales comps detail"
      onClick={onClose}
    >
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            marginBottom: '0.75rem',
            gap: '1rem',
          }}
        >
          <div>
            <h2 style={{ margin: 0, fontSize: '1.1rem' }}>Your comps — detail</h2>
            <div
              style={{
                fontSize: '0.8rem',
                color: 'var(--text-muted)',
                marginTop: '0.2rem',
                maxWidth: '48rem',
                lineHeight: 1.3,
              }}
            >
              Matched against: <em>{title}</em>
              {condition ? ` · condition: ${condition}` : ''}
              {categoryId ? ' · filtered by category' : ''}
            </div>
          </div>
          <button type="button" onClick={onClose} style={closeButtonStyle} aria-label="Close">
            ×
          </button>
        </div>

        <div
          style={{
            display: 'flex',
            gap: '0.5rem',
            marginBottom: '0.75rem',
            fontSize: '0.8rem',
            alignItems: 'center',
          }}
        >
          <span style={{ color: 'var(--text-muted)' }}>Sort by:</span>
          {(['similarity', 'date', 'price'] as SortKey[]).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setSortKey(k)}
              style={sortKey === k ? sortButtonActive : sortButtonStyle}
            >
              {k === 'similarity' ? 'Most similar' : k === 'date' ? 'Most recent' : 'Highest price'}
            </button>
          ))}
          <span style={{ marginLeft: 'auto', color: 'var(--text-muted)' }}>
            {rows ? `${rows.length} row${rows.length === 1 ? '' : 's'}` : ''}
          </span>
        </div>

        {loading && (
          <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
            Loading comps…
          </div>
        )}

        {error && (
          <div
            style={{
              padding: '0.75rem',
              background: 'rgba(220,38,38,0.08)',
              borderRadius: '0.375rem',
              color: 'var(--danger)',
              fontSize: '0.85rem',
            }}
          >
            {error}
          </div>
        )}

        {!loading && !error && sortedRows.length === 0 && (
          <div
            style={{
              padding: '2rem',
              textAlign: 'center',
              color: 'var(--text-muted)',
              fontSize: '0.9rem',
            }}
          >
            No matching comps found.
          </div>
        )}

        {!loading && !error && sortedRows.length > 0 && (
          <div style={{ overflowX: 'auto' }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  <th style={thStyle}>Match</th>
                  <th style={thStyle}>Title</th>
                  <th style={thStyle}>Brand</th>
                  <th style={thStyleNumeric}>Auction</th>
                  <th style={thStyleNumeric}>Date</th>
                  <th style={thStyleNumeric}>Sold</th>
                  <th style={thStyleNumeric}>MSRP</th>
                  <th style={thStyleNumeric}>Sell-thru</th>
                  <th style={thStyleNumeric}>Similarity</th>
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r) => (
                  <tr
                    key={r.id}
                    style={{
                      background: r.sold ? 'transparent' : 'rgba(180, 83, 9, 0.05)',
                    }}
                  >
                    <td style={tdStyle}>
                      <TierBadge tier={r.match_tier} label={r.match_tier_description} />
                    </td>
                    <td style={tdStyle}>
                      <div style={{ fontWeight: 500 }}>{r.title ?? '(untitled)'}</div>
                      {r.source_category_name && (
                        <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                          {r.source_category_name}
                          {!r.sold ? ' · did not sell' : ''}
                        </div>
                      )}
                    </td>
                    <td style={tdStyle}>
                      {r.brand ? (
                        <div style={{ fontWeight: 500 }}>{r.brand}</div>
                      ) : (
                        <span style={{ color: 'var(--text-muted)' }}>—</span>
                      )}
                      {r.mpn && (
                        <div
                          style={{
                            fontSize: '0.7rem',
                            color: 'var(--text-muted)',
                            fontFamily: 'ui-monospace, monospace',
                          }}
                        >
                          {r.mpn}
                        </div>
                      )}
                    </td>
                    <td style={tdStyleNumeric}>
                      {r.auction_number != null
                        ? `#${r.auction_number}${r.lot_number ? `·${r.lot_number}` : ''}`
                        : '—'}
                    </td>
                    <td style={tdStyleNumeric}>{shortDate(r.sold_at)}</td>
                    <td style={tdStyleNumeric}>
                      {r.final_price_cents != null && r.sold
                        ? formatCents(r.final_price_cents)
                        : '—'}
                    </td>
                    <td style={tdStyleNumeric}>
                      {r.msrp_cents != null ? formatCents(r.msrp_cents) : '—'}
                    </td>
                    <td style={tdStyleNumeric}>{formatRatio(r.sell_through_ratio)}</td>
                    <td style={tdStyleNumeric}>{formatSimilarity(r.similarity_score)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div
          style={{
            marginTop: '1rem',
            fontSize: '0.75rem',
            color: 'var(--text-muted)',
            lineHeight: 1.4,
          }}
        >
          Rows shaded amber did not sell. Capped at 100 most-similar matches. Sell-thru = sold price
          ÷ MSRP.
        </div>
      </div>
    </div>
  );
}

const backdropStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.55)',
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'center',
  padding: '2rem 1rem',
  zIndex: 120,
  overflowY: 'auto',
};

const modalStyle: React.CSSProperties = {
  background: 'var(--surface)',
  borderRadius: '0.5rem',
  padding: '1.25rem 1.5rem',
  maxWidth: '76rem',
  width: '100%',
  boxShadow: '0 20px 40px rgba(15, 23, 42, 0.2)',
};

const closeButtonStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  color: 'var(--text-muted)',
  fontSize: '1.5rem',
  cursor: 'pointer',
  lineHeight: 1,
  padding: '0 0.25rem',
};

const sortButtonStyle: React.CSSProperties = {
  padding: '0.25rem 0.6rem',
  border: '1px solid var(--border)',
  borderRadius: '0.25rem',
  background: 'transparent',
  color: 'var(--text)',
  fontSize: '0.8rem',
  cursor: 'pointer',
};

const sortButtonActive: React.CSSProperties = {
  ...sortButtonStyle,
  background: 'var(--accent, #1e2761)',
  color: 'white',
  borderColor: 'var(--accent, #1e2761)',
};

const tableStyle: React.CSSProperties = {
  width: '100%',
  borderCollapse: 'collapse',
  fontSize: '0.85rem',
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.5rem 0.6rem',
  borderBottom: '2px solid var(--border)',
  fontWeight: 600,
  color: 'var(--text-muted)',
  fontSize: '0.75rem',
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const thStyleNumeric: React.CSSProperties = {
  ...thStyle,
  textAlign: 'right',
  whiteSpace: 'nowrap',
};

const tdStyle: React.CSSProperties = {
  padding: '0.55rem 0.6rem',
  borderBottom: '1px solid var(--border)',
  verticalAlign: 'top',
};

const tdStyleNumeric: React.CSSProperties = {
  ...tdStyle,
  textAlign: 'right',
  whiteSpace: 'nowrap',
};
